import {
  Box,
  Container,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { SdpDashboardWrapper } from './SdpDashboard.styles';
import { useTranslation } from 'react-i18next';
import { DateRangeControl } from '../../components/Dashboard/DateRangeControl/DateRangeControl';
import { useDateRange } from '../../components/Dashboard/DateRangeControl/useDateRange';
import { SingleKpiBlock } from '../../components/Dashboard/SingleKpiBlock/SingleKpiBlock';
import { useEffect, useState } from 'react';
import { getActualRpKpis, getPercentKpisByPlanType, getPlanPreviousDateRangeCopy } from './helpers';
import { KPI_PLAN_TYPES, MD_SIZES, PLAN_PREVIOUS_DATE_RANGE_DATASET } from './constants';
import { PlanPreviousDateRangeDatasetKey } from './types';
import { BASE_FIELD_BACKGROUND_COLOR } from '../../themes/tokens';
import { useResponsive } from '../../hooks/useResponsive';
import { MultipleSalonsChart } from '../../components/Dashboard/Chart/MultipleSalonsChart/MultipleSalonsChart';
import { useSdpDashboardSettings } from './hooks/useSdpDashboardSettings';
import { KpisByPlanChart } from '../../components/Dashboard/Chart/KpisByPlanChart/KpisByPlanChart';
import { CustomLegend } from '../../components/Dashboard/Chart/CustomLegend/CustomLegend';
import { useDashboardAvgData } from './hooks/useDashboardAvgData';

export const SdpDashboard = () => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();

  const { dateRange, setDateRange } = useDateRange();

  const defaultPlanPreviousDateRange = Object.keys(
    PLAN_PREVIOUS_DATE_RANGE_DATASET,
  )[0] as PlanPreviousDateRangeDatasetKey;
  const [planPreviousDateRange, setPlanPreviousDateRange] =
    useState<PlanPreviousDateRangeDatasetKey>(defaultPlanPreviousDateRange);

  useEffect(() => {
    setPlanPreviousDateRange(defaultPlanPreviousDateRange);
  }, [dateRange]);

  const { settings } = useSdpDashboardSettings();
  const [salonClassificationFilter, setSalonClassificationFilter] = useState<undefined | string>();

  useEffect(() => {
    if (settings?.filters?.classification) {
      const defaultSalonClassification = Object.keys(settings.filters.classification)[0];
      setSalonClassificationFilter(defaultSalonClassification);
    }
  }, [settings]);

  const { avgData, isLoading } = useDashboardAvgData({
    dateRange: dateRange,
    salonClassification: salonClassificationFilter,
  });

  return (
    <Container
      disableGutters
      maxWidth={false}
      sx={{
        width: {
          xs: '90vw',
          sm: '90vw',
          md: '90vw',
        },
        maxWidth: '1650px',
        minWidth: '300px',
        margin: '16px auto',
      }}
    >
      <Box display="flex" justifyContent="center" mt="16px" mb="16px">
        <Typography variant={isMobile ? 'h4' : 'h2'} textAlign="center">
          {t('dashboard.sdpDashboardHeader')}
        </Typography>
      </Box>
      <SdpDashboardWrapper>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={5}>
            <Typography variant={isMobile ? 'h5' : 'h3'} fontWeight={300}>
              {t('dashboard.dashboardDataOverview')}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={12} md={3}>
            {salonClassificationFilter && (
              <FormControl fullWidth>
                <InputLabel>{t('dashboard.filter')}</InputLabel>
                <Select
                  label={t('dashboard.filter')}
                  value={salonClassificationFilter}
                  onChange={(e) => setSalonClassificationFilter(e.target.value)}
                  sx={{
                    minWidth: '25ch',
                    width: '100%',
                    backgroundColor: BASE_FIELD_BACKGROUND_COLOR,
                    '& .MuiSelect-select:focus': {
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  {Object.entries(settings.filters.classification).map(([key, label]) => {
                    return (
                      <MenuItem key={key} value={key}>
                        {label}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            )}
          </Grid>
          <Grid item xs={12} sm={12} md={4}>
            <DateRangeControl dateRange={dateRange} setDateRange={setDateRange} />
          </Grid>
        </Grid>

        <Grid container columns={7} spacing={2}>
          <Grid item xs={7} sm={7} md={1}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={12}>
                <SingleKpiBlock
                  label={t('dashboard.kpi.numberOfNewGuests')}
                  data={avgData.tnNewGuestsServiced?.presentDateRange.value.raw}
                  isLoading={isLoading?.tnNewGuestsServiced}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={12}>
                <SingleKpiBlock
                  label={t('dashboard.kpi.numberOfGuests')}
                  data={avgData.tnGuestsServiced?.presentDateRange.value.raw}
                  isLoading={isLoading?.tnGuestsServiced}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={7} sm={7} md={3}>
            <MultipleSalonsChart
              title={t('dashboard.salonDashboardTitle')}
              dateRange={dateRange}
              salonClassification={salonClassificationFilter}
            />
          </Grid>

          <Grid item sm={7} md={3}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <SingleKpiBlock
                  label={t('dashboard.kpi.numberOfServiceProviders')}
                  data={avgData.tnSp?.presentDateRange.value.raw}
                  isLoading={isLoading?.tnSp}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <SingleKpiBlock
                  label={t('dashboard.kpi.totalNumberServiceTransactions')}
                  data={avgData.tnServiceTransactions?.presentDateRange.value.raw}
                  isLoading={isLoading?.tnServiceTransactions}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <SingleKpiBlock
                  label={t('dashboard.kpi.totalNumberOfTransactions')}
                  data={avgData.tnTransactionsNumber?.presentDateRange.value.raw}
                  prevData={
                    avgData.tnTransactionsNumber?.previousDateRange?.value?.raw || undefined
                  }
                  isLoading={isLoading?.tnTransactionsNumber}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <SingleKpiBlock
                  label={t('dashboard.kpi.rpct')}
                  data={avgData.rpct?.presentDateRange.value.raw}
                  isLoading={isLoading?.rpct}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <SingleKpiBlock
                  label={t('dashboard.kpi.numberRetailOnlyTransactions')}
                  data={avgData.tnRetailOnlyTr?.presentDateRange.value.raw}
                  isLoading={isLoading?.tnRetailOnlyTr}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <SingleKpiBlock
                  label={t('dashboard.kpi.totalSales')}
                  data={
                    avgData.totalSales?.presentDateRange?.value
                      ? {
                          ...avgData.totalSales.presentDateRange.value.raw,
                          value: new Intl.NumberFormat('en-US').format(
                            avgData.totalSales.presentDateRange.value.raw.value,
                          ),
                        }
                      : undefined
                  }
                  isLoading={isLoading?.totalSales}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Divider />

        <Box display="flex" flexDirection="column" gap={2}>
          <Box
            display="flex"
            flexDirection={isMobile ? 'column' : 'row'}
            justifyContent="space-between"
          >
            <Select
              value={planPreviousDateRange}
              onChange={(e) =>
                setPlanPreviousDateRange(e.target.value as PlanPreviousDateRangeDatasetKey)
              }
              sx={{
                minWidth: '30ch',
                width: isMobile ? '100%' : 'fit-content',
                height: 'fit-content',
                backgroundColor: BASE_FIELD_BACKGROUND_COLOR,
                '& .MuiSelect-select:focus': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              {Object.entries(PLAN_PREVIOUS_DATE_RANGE_DATASET).map(([key, { label }]) => {
                if (key === 'previousQuarter' && dateRange.name !== 'QUARTER') return;

                return (
                  <MenuItem key={key} value={key}>
                    {label}{' '}
                    {(key === 'lastYear' || key === 'previousQuarter') &&
                      `(${getPlanPreviousDateRangeCopy(dateRange, key)})`}
                  </MenuItem>
                );
              })}
            </Select>
            <CustomLegend
              tooltipPreviousCopy={`${t(`dashboard.tooltipPreviousCopy.${planPreviousDateRange}`)}${
                planPreviousDateRange === 'lastYear' || planPreviousDateRange === 'previousQuarter'
                  ? ` (${getPlanPreviousDateRangeCopy(dateRange, planPreviousDateRange)})`
                  : ''
              }`}
            />
          </Box>
          <Grid container spacing={2}>
            {KPI_PLAN_TYPES.map((planType) => {
              const percentKpisByPlanType = getPercentKpisByPlanType({
                kpiPlanType: planType.id,
                data: avgData,
                planPreviousDateRange: planPreviousDateRange,
              });

              return (
                <Grid item xs={12} sm={6} md={MD_SIZES[planType.id]} key={planType.id}>
                  <KpisByPlanChart
                    planTitle={planType.label}
                    data={percentKpisByPlanType}
                    tooltipPreviousCopy={t(
                      `dashboard.tooltipPreviousCopy.${planPreviousDateRange}`,
                    )}
                  />
                </Grid>
              );
            })}
            {(() => {
              const actualRpKpis = getActualRpKpis({
                data: avgData,
                planPreviousDateRange: planPreviousDateRange,
              });

              return (
                <Grid item xs={12} sm={6} md={MD_SIZES.rp} key={'rp'}>
                  <KpisByPlanChart
                    data={actualRpKpis}
                    tooltipPreviousCopy={t(
                      `dashboard.tooltipPreviousCopy.${planPreviousDateRange}`,
                    )}
                  />
                </Grid>
              );
            })()}
          </Grid>
        </Box>
      </SdpDashboardWrapper>
    </Container>
  );
};
